import React, { Suspense } from "react";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LocaleProvider } from "@/hooks/useLocale";
import { NotificationsProvider } from "@/providers/NotificationsProvider";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Waitlist from "./pages/Waitlist";
import NotFound from "./pages/NotFound";
import AuthGuard from "./components/auth/AuthGuard";
import { SessionExpiredDialog } from "./components/auth/SessionExpiredDialog";
import { ScrollToTop } from "./components/ScrollToTop";
import { installAuthErrorInterceptor } from "./lib/auth-error-interceptor";
import { ImpersonationBar } from "./components/layout/ImpersonationBar";
import { useDailyLoginBonus } from "./hooks/useDailyLoginBonus";
import { useDemoWorldSeed } from "./hooks/useDemoWorldSeed";
import MessageTestPage from "./pages/MessageTestPage";

// Heavy pages lazy-loaded to keep the initial bundle small.
// maplibre-gl + pmtiles (~2 MB) are pulled in by WorldBuilder and Play;
// recharts (~400 KB) is pulled in by Settings (superadmin analytics tab).
// W15: static marketing/utility pages also lazy-loaded — they're off the hot path
// and every eager import adds bytes to the initial JS chunk.
const Settings = React.lazy(() => import("./pages/Settings"));
const WorldBuilder = React.lazy(() => import("./pages/WorldBuilder"));
const EntityPage = React.lazy(() => import("./pages/EntityPage"));
const Play = React.lazy(() => import("./pages/Play"));
const JoinAdventure = React.lazy(() => import("./pages/JoinAdventure"));
const UserProfile = React.lazy(() => import("./pages/UserProfile"));
const Community = React.lazy(() => import("./pages/Community"));
const Messages = React.lazy(() => import("./pages/Messages"));
const AdminUsers = React.lazy(() => import("./pages/AdminUsers"));
const Explore = React.lazy(() => import("./pages/Explore"));
const WorldPreview = React.lazy(() => import("./pages/WorldPreview"));
const Profile = React.lazy(() => import("./pages/Profile"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const About = React.lazy(() => import("./pages/About"));
const SupportUs = React.lazy(() => import("./pages/SupportUs"));
const Roadmap = React.lazy(() => import("./pages/Roadmap"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Terms = React.lazy(() => import("./pages/Terms"));
const Unsubscribe = React.lazy(() => import("./pages/Unsubscribe"));

// Install global auth error interceptor
installAuthErrorInterceptor();

/** Mounts the daily-login-bonus listener. Rendered outside all route components so it stays
 *  mounted for the entire authenticated session. Returns null — no UI output. */
function DailyLoginBonusProvider() {
  useDailyLoginBonus();
  return null;
}

/** Clones the configured demo world into a new user's account on their very first
 *  sign-in (when they have 0 worlds). No-ops if app_settings.demo_world_id is
 *  not set — operators enable this by updating that row. */
function DemoWorldSeedProvider() {
  useDemoWorldSeed();
  return null;
}

const App = () => (
  <ErrorBoundary>
  <ThemeProvider attribute="class" defaultTheme="dark" storageKey="loreweaver-theme">
    <BrowserRouter>
      <LocaleProvider>
        {/* P-18: NotificationsProvider wraps the whole tree so the Realtime
            channel persists across page navigations (AppHeader remounts but
            NotificationBell now reads from stable provider context) */}
        <NotificationsProvider>
          <TooltipProvider>
            <Sonner />
            {/* I-08: aria-live region — screen readers announce status updates
                (toast confirmations, errors, loading states) without focus loss.
                Components push messages via document.getElementById("av-live-region")?.textContent = msg */}
            <div
              id="av-live-region"
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            />
            <SessionExpiredDialog />
            <ScrollToTop />
            <ImpersonationBar />
            <DailyLoginBonusProvider />
            <DemoWorldSeedProvider />
            <Suspense fallback={null}>
              <Routes>
                {/* H10: per-route ErrorBoundary so a crash in one route doesn't
                    unmount the entire app — light inline fallback keeps URL usable */}
                <Route path="/" element={<ErrorBoundary inline><AuthGuard><Index /></AuthGuard></ErrorBoundary>} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/waitlist" element={<Waitlist />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/about" element={<About />} />
                <Route path="/w/:worldId" element={<WorldPreview />} />
                <Route path="/support" element={<SupportUs />} />
                <Route path="/roadmap" element={<Roadmap />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/profile" element={<ErrorBoundary inline><AuthGuard><Profile /></AuthGuard></ErrorBoundary>} />
                <Route path="/settings" element={<ErrorBoundary inline><AuthGuard><Settings /></AuthGuard></ErrorBoundary>} />
                <Route path="/world/:worldId" element={<ErrorBoundary inline><AuthGuard><WorldBuilder /></AuthGuard></ErrorBoundary>} />
                <Route path="/world/:worldId/entity/:entityId" element={<ErrorBoundary inline><AuthGuard><EntityPage /></AuthGuard></ErrorBoundary>} />
                <Route path="/play/:adventureId" element={<ErrorBoundary inline><AuthGuard><Play /></AuthGuard></ErrorBoundary>} />
                <Route path="/play/:adventureId/entity/:entityId" element={<ErrorBoundary inline><AuthGuard><EntityPage isGameplay={true} /></AuthGuard></ErrorBoundary>} />
                <Route path="/join/:inviteCode" element={<ErrorBoundary inline><AuthGuard><JoinAdventure /></AuthGuard></ErrorBoundary>} />
                <Route path="/join" element={<ErrorBoundary inline><AuthGuard><JoinAdventure /></AuthGuard></ErrorBoundary>} />
                <Route path="/user/:userId" element={<ErrorBoundary inline><AuthGuard><UserProfile /></AuthGuard></ErrorBoundary>} />
                <Route path="/community" element={<ErrorBoundary inline><AuthGuard><Community /></AuthGuard></ErrorBoundary>} />
                <Route path="/messages" element={<ErrorBoundary inline><AuthGuard><Messages /></AuthGuard></ErrorBoundary>} />
                <Route path="/admin/users" element={<ErrorBoundary inline><AuthGuard><AdminUsers /></AuthGuard></ErrorBoundary>} />
                {/* Dev-only: message UI test lab — no auth required */}
                <Route path="/dev/message-test" element={<MessageTestPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </TooltipProvider>
        </NotificationsProvider>
      </LocaleProvider>
    </BrowserRouter>
  </ThemeProvider>
  </ErrorBoundary>
);

export default App;
